import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Box, ListItemText, MenuItem, Menu, Button, Typography, CircularProgress, IconButton, Tooltip, Divider, Chip } from '@mui/material';
import { Notification } from '@prisma/client/browser';
import { useUser } from '@auth0/nextjs-auth0/client';
import axios from 'axios';
import { useToast } from '@/app/context/toast-provider';
import { DoneAll, CheckCircleOutline, DeleteOutline, FiberManualRecord } from '@mui/icons-material';
const NotificationsPanel = ({
  isOpen = false,
  onClose,
  anchorEl,
  onCountChange
}) => {
  const {
    showSuccessToast
  } = useToast();
  const {
    isLoading: isUserLoading,
    user
  } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const getNotifications = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/notifications`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = response.data;
      setNotifications(data.data);
      const unread = data.data.filter(n => !n.isRead).length;
      if (onCountChange) onCountChange(unread);
    } catch (_err) {
      // ignore error
    } finally {
      setIsLoading(false);
    }
  };
  const markAllAsRead = async () => {
    try {
      await axios.put(`/api/notifications`, {}, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      showSuccessToast('All notifications marked as read');
      if (onCountChange) onCountChange(0);
      getNotifications();
    } catch (err) {
      console.error('Error marking notifications as read: ', err);
    }
  };
  const markAsRead = async notificationId => {
    try {
      await axios.put(`/api/notifications?notificationId=${notificationId}`, {}, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      getNotifications();
    } catch (err) {
      console.error('Error marking notification as read: ', err);
    }
  };
  const deleteNotification = async notificationId => {
    try {
      await axios.delete(`/api/notifications/${notificationId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      showSuccessToast('Notification deleted');
      getNotifications();
    } catch (err) {
      console.error('Error deleting notification: ', err);
    }
  };
  useEffect(() => {
    if (user && !isUserLoading) {
      getNotifications();
    }
  }, [user, isUserLoading, isOpen]);
  useEffect(() => {
    if (!user || isUserLoading) {
      onCountChange?.(0);
      return;
    }
    let prevCount = null;
    let lastShownNotificationId = null;
    let cancelled = false;
    const fetchUnreadCount = async () => {
      if (!user || cancelled) return;
      try {
        const res = await axios.get('/api/notifications?unreadCount=true');
        if (cancelled) return;
        const newCount = res.data.count ?? 0;
        const latest = res.data.latestNotification;
        if (latest && latest.link?.includes('tab=find-talent')) {
          if (prevCount !== null && latest.id !== lastShownNotificationId) {
            showSuccessToast(latest.message);
          }
          lastShownNotificationId = latest.id;
        }
        prevCount = newCount;
        onCountChange?.(newCount);
      } catch {
        // ignore
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, isUserLoading, onCountChange]);
  const unreadCount = notifications.filter(n => !n.isRead).length;
  return <Menu id="notification-menu" open={isOpen} onClose={onClose} MenuListProps={{
    'aria-labelledby': 'basic-button',
    sx: {
      py: 0,
      maxHeight: 'none',
      overflow: 'visible'
    }
  }} anchorEl={anchorEl} transformOrigin={{
    horizontal: 'left',
    vertical: 'top'
  }} anchorOrigin={{
    horizontal: 'right',
    vertical: 'top'
  }} sx={{
    '& .MuiPaper-root': {
      borderRadius: '12px',
      mt: 1,
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
    }
  }}>
            <div className="w-96! max-h-[480px] flex flex-col overflow-hidden">
                <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        px: 2,
        py: 1.5,
        mb: 0.5,
        borderBottom: '1px solid var(--border)',
        bgcolor: 'var(--page)'
      }}>
                    <Typography component="div" sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: 'var(--foreground)'
        }}>
                        Notifications
                        {unreadCount > 0 && <Chip label={`${unreadCount} new`} size="x-small" variant="soft" color="brand" className="ml-1! -mt-0.5!" />}
                    </Typography>
                    {unreadCount > 0 && <Button variant="contained" color="neutral" onClick={markAllAsRead} size="x-small" startIcon={<DoneAll sx={{
          fontSize: 14
        }} />} sx={{
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '13px'
        }}>
                            Mark all read
                        </Button>}
                </Box>

                <Box sx={{
        overflowY: 'auto',
        flex: 1,
        minHeight: 0
      }}>
                    {isLoading && <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6
        }}>
                            <CircularProgress size={24} sx={{
            color: 'var(--primary)'
          }} />
                        </Box>}

                    {!isLoading && notifications.length === 0 && <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 2
        }}>
                            <Typography sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--foreground-subtle)'
          }}>No notifications yet</Typography>
                            <Typography sx={{
            fontSize: '13px',
            color: 'var(--foreground-subtle)',
            mt: 0.5
          }}>Updates will appear here</Typography>
                        </Box>}

                    {!isLoading && notifications.map((notification, index) => <div key={`container-+${notification.id}`}>
                                <MenuItem key={notification.id} sx={{
            ml: 1,
            mr: 0.5,
            my: notification.isRead ? 0 : 0.5,
            borderRadius: '8px',
            px: 1.5,
            py: 1,
            bgcolor: notification.isRead ? 'transparent' : 'var(--brand-soft)',
            '&:hover': {
              bgcolor: notification.isRead ? 'var(--hover)' : 'var(--brand-soft)'
            }
          }} disableRipple>
                                    <div className="flex items-start gap-3 w-full">
                                        <FiberManualRecord sx={{
                fontSize: 10,
                mt: 0.6,
                color: notification.isRead ? 'var(--border)' : 'var(--primary)',
                flexShrink: 0
              }} />
                                        <ListItemText onClick={() => {
                if (notification.link) {
                  window.open(notification.link, '_blank');
                }
                if (!notification.isRead) {
                  markAsRead(notification.id);
                }
              }} sx={{
                m: 0,
                minWidth: 0,
                flex: '1 1 auto',
                cursor: notification.link ? 'pointer' : 'default'
              }} primary={<Typography sx={{
                fontSize: '12px',
                fontWeight: notification.isRead ? 400 : 500,
                color: 'var(--foreground)',
                lineHeight: '18px',
                wordBreak: 'break-word',
                whiteSpace: 'pre-line'
              }}>
                                                    {notification.message}
                                                </Typography>} secondary={<div className="w-full flex items-center justify-between">
                                                    <Typography sx={{
                  fontSize: '11px',
                  color: 'var(--foreground-subtle)',
                  display: 'block'
                }}>
                                                        {moment(notification.createdAt).fromNow()}
                                                    </Typography>

                                                    <div className="flex gap-0.5 flex-shrink-0">
                                                        {!notification.isRead && <Tooltip title="Mark as read" arrow>
                                                                <IconButton size="small" onClick={() => markAsRead(notification.id)} sx={{
                      color: 'var(--primary)',
                      '&:hover': {
                        bgcolor: 'var(--brand-soft)'
                      }
                    }}>
                                                                    <CheckCircleOutline sx={{
                        fontSize: 16
                      }} />
                                                                </IconButton>
                                                            </Tooltip>}
                                                        <Tooltip title="Delete" arrow>
                                                            <IconButton size="small" onClick={() => deleteNotification(notification.id)} sx={{
                      color: 'var(--foreground-subtle)',
                      '&:hover': {
                        color: '#EF4444',
                        bgcolor: 'color-mix(in srgb, #EF4444 10%, var(--page))'
                      }
                    }}>
                                                                <DeleteOutline sx={{
                        fontSize: 16
                      }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </div>} slotProps={{
                secondary: {
                  component: 'div'
                }
              }} />
                                    </div>
                                </MenuItem>
                                {index < notifications.length - 1 && notification.isRead && <Divider key={`divider-${notification.id}`} sx={{
            mx: 1,
            my: '0px !important'
          }} />}
                            </div>)}
                </Box>
            </div>
        </Menu>;
};
export default NotificationsPanel;